import { useCallback, useEffect, useRef, useState } from 'react';
import { EntraIdConfig } from '../config/general.types';
import { apiClient } from '../services/apiClient';

export const GENERAL_CONFIG_UPDATED_EVENT = 'adm:general-config-updated';

let publishedConfigRevision = 0;
let inFlightConfigRequest: Promise<EntraIdConfig | undefined> | null = null;

export const publishGeneralConfig = (config: EntraIdConfig) => {
  publishedConfigRevision += 1;
  globalThis.dispatchEvent(new CustomEvent<EntraIdConfig>(GENERAL_CONFIG_UPDATED_EVENT, { detail: config }));
};

const requestGeneralConfig = async (): Promise<EntraIdConfig | undefined> => {
  if (inFlightConfigRequest) {
    return inFlightConfigRequest;
  }

  const revisionAtRequestStart = publishedConfigRevision;
  inFlightConfigRequest = (async () => {
    const response = await apiClient.get<EntraIdConfig>('/general/config');
    const configData = response.data ?? response;

    if (publishedConfigRevision !== revisionAtRequestStart) {
      return undefined;
    }

    publishGeneralConfig(configData);
    return configData;
  })();

  try {
    return await inFlightConfigRequest;
  } finally {
    inFlightConfigRequest = null;
  }
};

export const useGeneralConfig = () => {
  const [config, setConfig] = useState<EntraIdConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const activeLoadCountRef = useRef(0);

  useEffect(() => {
    const handleConfigUpdate = (event: Event) => {
      setConfig((event as CustomEvent<EntraIdConfig>).detail);
      if (activeLoadCountRef.current === 0) {
        setLoadingConfig(false);
      }
    };

    globalThis.addEventListener(GENERAL_CONFIG_UPDATED_EVENT, handleConfigUpdate);
    return () => globalThis.removeEventListener(GENERAL_CONFIG_UPDATED_EVENT, handleConfigUpdate);
  }, []);

  const loadConfig = useCallback(async () => {
    activeLoadCountRef.current += 1;
    try {
      setLoadingConfig(true);
      const configData = await requestGeneralConfig();
      if (configData) {
        setConfig(configData);
      }
    } catch (error) {
      console.error('Failed to load general config:', error);
    } finally {
      activeLoadCountRef.current -= 1;
      if (activeLoadCountRef.current === 0) {
        setLoadingConfig(false);
      }
    }
  }, []);

  return {
    config,
    loadingConfig,
    loadConfig
  };
};
